import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Utils from "../../utils";
import { runbookExecutionHistoryActions } from "../../actions/runbookExecutionHistory.actions";
import { runbookExecutionHistoryService } from "../../services/runbookExecutionHistory.service";
import Grid from "../common/Grid";
import Title from "../common/Title";
import { CiBoxList } from "react-icons/ci";
import { MdError } from "react-icons/md";
import Subtitle from "../common/Subtitle";
import useRunbookTitle from "../../hooks/runbookTitle";
import { JsonView } from "react-json-view-lite";
import "react-json-view-lite/dist/index.css";
import GridHelper from "../helpers/gridHelper";
import ExportToExcelButton from "../common/ExportToExcelButton";
import RunbookExecutionHistoryLineGridFilter from "./RunbookExecutionHistoryLineGridFilter";
import { Button, Modal } from "react-bootstrap";
import { FaEye } from "react-icons/fa";
import ContactSupportExecution from "./ContactSupportExecution";
import GraceBotBubble from "./executionAssist/GraceBotBubble";

const filterInitialState = {
  // "" (falsy) shows every line; "true" shows only error lines — same wire format the
  // standalone "Include logs" checkbox used to send before it became a FilterBar chip.
  error: "",
  // Multi-select — comma-separated on the wire (see Utils.objectToQueryString), matching
  // RunbookExecutionHistoryGrid's `status` filter.
  stepNumber: [],
  recordNo: "",
  errorMessage: "",
  sourceData: "",
};

// Same reasoning as RunbookExecutionHistoryGrid: an explicit `executionId` prop means this
// instance owns its own identity, paging, sort, filter, and fetched data instead of sharing
// the single global URL + Redux slice — needed so a workspace window host can have several
// Execution History Lines windows open at once, one per execution. The standalone routed
// page (no executionId prop) keeps the original URL+Redux-driven behavior unchanged.
function RunbookExecutionHistoryLineGrid(props) {
  const { hideTitle, runbookId: runbookIdProp, executionId: executionIdProp } = props;
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const windowed = executionIdProp !== undefined;

  const runbookExecutionHistoryLineReducer = useSelector(
    (state) => state.runbookExecutionHistoryLine
  );

  const actions = [];

  const [filter, setFilter] = useState(filterInitialState);
  const [showJsonModal, setShowJsonModal] = useState(false);
  const [selectedJsonData, setSelectedJsonData] = useState(null);

  // Windowed-only local copies of what the URL/Redux slice hold for the standalone route.
  const [localPageNumber, setLocalPageNumber] = useState(1);
  const [localOrderBy, setLocalOrderBy] = useState(undefined);
  const [localOrderDirection, setLocalOrderDirection] = useState(undefined);
  const [localResult, setLocalResult] = useState(null);
  const [localLoading, setLocalLoading] = useState(windowed);

  const fetchWindowedLines = useCallback(() => {
    setLocalLoading(true);
    const params = {
      executionId: executionIdProp,
      ...(runbookIdProp ? { runbookId: runbookIdProp } : {}),
      clientId: props.user.clientId,
      clientGuid: props.user.clientGuid,
      pageNumber: localPageNumber,
      ...(localOrderBy ? { order_by: localOrderBy, order_direction: localOrderDirection } : {}),
    };
    Object.entries(filter).forEach(([key, value]) => {
      const hasValue = Array.isArray(value) ? value.length > 0 : !!value;
      if (hasValue) params[key] = value;
    });
    runbookExecutionHistoryService.getHistoryLine(params).then(
      (response) => {
        setLocalResult(response.data);
        setLocalLoading(false);
      },
      () => setLocalLoading(false)
    );
  }, [
    executionIdProp,
    runbookIdProp,
    localPageNumber,
    localOrderBy,
    localOrderDirection,
    filter,
    props.user.clientId,
    props.user.clientGuid,
  ]);

  // Standalone route: fetch is entirely URL-driven, matching the pre-existing behavior.
  useEffect(() => {
    if (windowed) return;
    let queryParams = Utils.getQueryStringParams(location);
    queryParams["clientId"] = props.user.clientId;
    queryParams["clientGuid"] = props.user.clientGuid;

    dispatch(runbookExecutionHistoryActions.getHistoryLine(queryParams));

    setFilter({
      error: queryParams["error"] || "",
      // Sent/read as a single comma-separated value (see FilterBar/objectToQueryString) —
      // split back into the array the multiselect filter works with.
      stepNumber: queryParams["stepNumber"] ? queryParams["stepNumber"].split(",") : [],
      recordNo: queryParams["recordNo"] || "",
      errorMessage: queryParams["errorMessage"] || "",
      sourceData: queryParams["sourceData"] || "",
    });
    // eslint-disable-next-line
  }, [location, windowed]);

  // Windowed: (re)fetch whenever this instance's OWN identity/paging/sort/filter changes.
  useEffect(() => {
    if (!windowed) return;
    fetchWindowedLines();
  }, [windowed, fetchWindowedLines]);

  const handleSearch = (nextFilter) => {
    if (windowed) {
      setFilter(nextFilter);
      setLocalPageNumber(1);
      return;
    }
    GridHelper.handleSearch(nextFilter, location, navigate);
  };

  let displayColumns = [
    "stepNumber",
    "recordNo",
    "error",
    "errorMessage",
    "sourceData",
    "transDateTime",
  ];

  // Display-only header text. Sorting still keys off the column names above ("error",
  // "errorMessage"), so the server-side sort params are unchanged.
  const columnLabels = { error: "Type", errorMessage: "Message" };

  // Server-side filtering is now handled by the API
  const rawData = (windowed ? localResult?.data : runbookExecutionHistoryLineReducer.paginatedResult?.data) || [];
  const filteredRawData = rawData; // No client-side filtering needed

  const executionId = windowed ? executionIdProp : Utils.getQueryStringParams(location)["executionId"];

  // Checkbox options for the Step Number filter, accumulated across every fetch for this
  // execution (including the initial unfiltered load) rather than read fresh off just the
  // currently-visible page. Deriving straight from `rawData` meant applying a Step Number
  // filter narrowed the fetched page down to only the selected value(s), so reopening the
  // filter to change it showed just that one option instead of the original full list. Reset
  // when the execution changes; still free-typable for anything never seen.
  const [seenStepValues, setSeenStepValues] = useState(() => new Set());

  useEffect(() => {
    setSeenStepValues(new Set());
  }, [executionId]);

  useEffect(() => {
    if (rawData.length === 0) return;
    setSeenStepValues((prev) => {
      const next = new Set(prev);
      rawData.forEach((l) => {
        if (l.stepNumber !== null && l.stepNumber !== undefined && l.stepNumber !== "") next.add(l.stepNumber);
      });
      return next;
    });
  }, [rawData]);

  const stepOptions = useMemo(() => [...seenStepValues], [seenStepValues]);

  const data = filteredRawData.map(
    (l) => {
      return {
        ...l,
        recordNo: <div style={{ textAlign: "center" }}>{l.recordNo}</div>,
        transDateTime: Utils.convertToDateTime(l.transDateTime),
        // Type column: the `error` flag as a word — "Error" for 1/true, "Log" for 0/false.
        error: l.error ? <span className="text-danger fw-semibold">Error</span> : "Log",
        sourceData: l.sourceData ? (
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => {
              setSelectedJsonData(JSON.parse(l.sourceData));
              setShowJsonModal(true);
            }}
          >
            <FaEye className="me-1" />
            View JSON
          </Button>
        ) : (
          ""
        ),
      };
    }
  );
  const historyLine = rawData?.[0];
  const { runbookTitle, runbookTitleLoading } = useRunbookTitle(
    historyLine?.runbookId ?? (windowed ? runbookIdProp : undefined)
  );

  // Fetch all records for Excel export
  const fetchAllDataForExport = useCallback(async () => {
    const params = windowed
      ? {
          executionId: executionIdProp,
          ...(runbookIdProp ? { runbookId: runbookIdProp } : {}),
          clientId: props.user.clientId,
          clientGuid: props.user.clientGuid,
          pageSize: 10000,
          pageNumber: 1,
        }
      : {
          ...Utils.getQueryStringParams(location),
          clientId: props.user.clientId,
          clientGuid: props.user.clientGuid,
          pageSize: 10000, // Large page size to get all records
          pageNumber: 1,
        };

    if (windowed) {
      Object.entries(filter).forEach(([key, value]) => {
        const hasValue = Array.isArray(value) ? value.length > 0 : !!value;
        if (hasValue) params[key] = value;
      });
    }

    const response = await runbookExecutionHistoryService.getHistoryLine(params);
    return response.data?.data || [];
  }, [windowed, executionIdProp, runbookIdProp, filter, location, props.user.clientId, props.user.clientGuid]);

  const loading = windowed ? localLoading : runbookExecutionHistoryLineReducer.loading;
  const pageNumber = (windowed ? localResult?.pageNumber : runbookExecutionHistoryLineReducer.paginatedResult?.pageNumber) ?? 1;
  const totalPages = (windowed ? localResult?.totalPages : runbookExecutionHistoryLineReducer.paginatedResult?.totalPages) ?? 0;

  const handlePageChange = windowed
    ? (page) => setLocalPageNumber(page + 1)
    : GridHelper.handlePageClick;
  const handleSort = windowed
    ? (orderBy, orderDirection) => {
        setLocalOrderBy(orderBy);
        setLocalOrderDirection(orderDirection);
      }
    : GridHelper.handleSortClick;

  return (
    <>
      {!hideTitle && (
        <Title
          text="Execution History Lines"
          icon={<MdError className="mb-1" />}
        />
      )}
      {!runbookTitleLoading && !loading && (
        <Subtitle
          text={`Execution of ${runbookTitle} (${executionId})`}
          icon={<CiBoxList className="mb-1" />}
        />
      )}

      <div className="d-flex gap-3 align-items-center mb-3 p-3 bg-light rounded">
        {filteredRawData.length > 0 && (
          <ExportToExcelButton
            data={filteredRawData}
            filename={`ExecutionHistory_${executionId}.xlsx`}
            fetchAllData={fetchAllDataForExport}
          />
        )}

        <ContactSupportExecution
          runbookName={runbookTitle}
          executionId={executionId}
          startTime={rawData?.[0]?.transDateTime ? Utils.convertToDateTime(rawData[0].transDateTime) : ""}
          endTime={rawData?.[rawData.length - 1]?.transDateTime ? Utils.convertToDateTime(rawData[rawData.length - 1].transDateTime) : ""}
          user={props.user}
        />
      </div>

      <RunbookExecutionHistoryLineGridFilter
        filter={filter}
        stepOptions={stepOptions}
        onSearch={windowed ? handleSearch : undefined}
      />

      <Grid
        data={data}
        displayColumns={displayColumns}
        columnLabels={columnLabels}
        actions={actions}
        pageNumber={pageNumber - 1}
        totalPages={totalPages}
        loading={loading || runbookTitleLoading}
        onPageChange={handlePageChange}
        onSort={handleSort}
      ></Grid>

      <Modal
        show={showJsonModal}
        onHide={() => setShowJsonModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Source Data</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedJsonData && (
            <JsonView
              data={selectedJsonData}
              shouldExpandNode={() => true}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowJsonModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {executionId && (
        <GraceBotBubble
          clientId={props.user.clientId}
          executionId={executionId}
          runbookId={historyLine?.runbookId ?? (windowed ? runbookIdProp : undefined)}
          runbookName={runbookTitle}
        />
      )}
    </>
  );
}

export default RunbookExecutionHistoryLineGrid;
